import { Box, Divider, IconButton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useGetIdentity, usePermissions, UserIdentity } from "react-admin";
import { useComments } from "@/hooks/useComments";
import { useUserLookup } from "@/hooks/useUserLookup";
import { useActivePartner } from "@/hooks/useActivePartner";
import { hasPermission } from "@/utils/permissions";
import Button from "@/components/ui/Button";
import { UIComponentSize } from "@/types/shared/uiComponents";
import { ResourceName } from "@/types";
import { getUserId } from "@/resources/tasks/utils/commentUtils";
import { getUserDisplayInfo } from "@/resources/tasks/utils/taskUtils";
import { CommentInput } from "@/resources/tasks/components/task-comments/CommentInput";
import { CommentItem } from "./CommentItem";
import type { Comment } from "@/types/comment";

interface RecordCommentsProps {
  /** Subject record type (ADR-052 subject_type); the API path segment, typed so embeds are checked. */
  subjectType: ResourceName;
  /** Subject record id. */
  subjectId: string | undefined;
}

/**
 * Subject-agnostic comment thread (ADR-052 S4). Renders the polymorphic comments
 * tree via useComments: top-level comments with one level of nested replies, plus
 * create / inline-edit / reply / delete. Author identity resolves from each
 * comment's created_by UUID.
 *
 * Fills its container as a flex column: the thread scrolls while the single
 * composer stays pinned to the bottom. "Reply" retargets that one composer (a
 * banner shows who you're replying to) rather than opening a separate input.
 */
// Collapse a thread once it has more than this many replies; the rest sit behind
// a "show earlier replies" toggle so long threads don't dominate the panel.
const REPLY_COLLAPSE_THRESHOLD = 3;

export function RecordComments({ subjectType, subjectId }: RecordCommentsProps) {
  const [draft, setDraft] = useState("");
  // The comment being replied to (null = composing a new top-level comment).
  const [replyTarget, setReplyTarget] = useState<Comment | null>(null);
  // Thread ids whose older replies the user has expanded.
  const [expandedThreads, setExpandedThreads] = useState<ReadonlySet<string>>(new Set());

  const toggleThread = useCallback((id: string) => {
    setExpandedThreads((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const { data: identityData } = useGetIdentity();
  const currentUser = identityData as UserIdentity | undefined;
  const { getUser, getUserSync } = useUserLookup();

  // Read-only partner (AEGL/ACAP, ADR-035): hide all write affordances. The server
  // also enforces this; this is UI gating only.
  const { readOnly } = useActivePartner();
  const canWrite = !!currentUser && !readOnly;

  // Moderator delete-any: the server allows the author OR a partner admin to delete
  // (checkCommentAuthor -> partners:admin). Mirror that exact rule here so the affordance
  // matches enforcement across every subject (clients + tasks) — author-only would hide a
  // delete the API would honor; granting more than partners:admin would 403. Edit stays
  // author-only (moderation removes, it doesn't rewrite someone else's words).
  const { permissions } = usePermissions();
  const isPartnerAdmin = hasPermission(permissions, "partners", "admin");

  const {
    comments,
    error,
    createComment,
    posting,
    refetch,
    editComment,
    editingCommentId,
    replyComment,
    replyingToId,
    deleteComment,
    deletingCommentId,
  } = useComments(subjectType, subjectId);

  const currentUserId = useMemo(() => getUserId(currentUser), [currentUser]);

  // On open, land at the newest comment (bottom) rather than the top. One-shot:
  // jump once after the first load so later refetches don't yank the user down
  // while they're reading. The component remounts each time the drawer opens, so
  // the flag naturally resets per open.
  const scrollRef = useRef<HTMLDivElement>(null);
  const didInitialScroll = useRef(false);
  useEffect(() => {
    if (didInitialScroll.current || comments.length === 0 || !scrollRef.current) return;
    const el = scrollRef.current;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    didInitialScroll.current = true;
  }, [comments.length]);

  // Pre-fetch author users for top-level comments AND their replies (created_by is a UUID).
  useEffect(() => {
    const ids = new Set<string>();
    if (currentUserId) ids.add(currentUserId);
    const collect = (c: Comment) => {
      if (c.created_by && !c.created_by.match(/^0+$/)) ids.add(c.created_by);
      (c.replies ?? []).forEach(collect);
    };
    comments.forEach(collect);
    ids.forEach((id) => {
      if (!getUserSync(id)) void getUser(id);
    });
  }, [currentUserId, comments, getUserSync, getUser]);

  const submitting = posting || replyingToId !== null;

  // One composer for both new comments and replies; replyTarget decides which.
  const handleSubmit = useCallback(async () => {
    const text = draft.trim();
    if (!text) return;
    try {
      if (replyTarget) {
        await replyComment(replyTarget.id, text);
        setReplyTarget(null);
      } else {
        await createComment(text);
      }
      setDraft("");
    } catch (err) {
      console.error("Failed to submit comment:", err);
    }
  }, [draft, replyTarget, replyComment, createComment]);

  const isMyComment = useCallback(
    (createdBy: string | undefined): boolean => !!currentUserId && createdBy === currentUserId,
    [currentUserId]
  );

  const authorName = (c: Comment): string =>
    getUserDisplayInfo(c.created_by ? getUserSync(c.created_by) : null).displayName;

  const renderItem = (comment: Comment, isReply: boolean) => {
    const isOwn = isMyComment(comment.created_by);
    return (
      <CommentItem
        key={comment.id}
        comment={comment}
        isCurrentUserComment={isOwn}
        user={comment.created_by ? getUserSync(comment.created_by) : null}
        canDeleteComment={(isOwn || isPartnerAdmin) && !readOnly}
        onDelete={deleteComment}
        isDeletingThis={deletingCommentId === comment.id}
        canEditComment={isOwn && !readOnly}
        onEdit={editComment}
        isSavingEdit={editingCommentId === comment.id}
        showReplyAction={!isReply && canWrite}
        onReplyClick={() => setReplyTarget(comment)}
      />
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {error && (
        <Box my={2} sx={{ flexShrink: 0 }}>
          <Typography variant="body2" color="error" mb={1}>
            Failed to load comments: {error}
          </Typography>
          <Button size={UIComponentSize.Small} onClick={refetch}>
            Retry
          </Button>
        </Box>
      )}

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pr: 1,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
          "&::-webkit-scrollbar-thumb": { bgcolor: "divider", borderRadius: 3 },
        }}
      >
        {comments.length > 0 ? (
          <Stack spacing={2}>
            {comments.map((comment) => {
              const replies = comment.replies ?? [];
              const expanded = expandedThreads.has(comment.id);
              const collapsed = replies.length > REPLY_COLLAPSE_THRESHOLD && !expanded;
              const hiddenCount = replies.length - REPLY_COLLAPSE_THRESHOLD;
              // Collapsed: show the most recent few; the older ones hide behind the toggle.
              const shownReplies = collapsed ? replies.slice(-REPLY_COLLAPSE_THRESHOLD) : replies;
              return (
                <Box key={comment.id}>
                  {renderItem(comment, false)}

                  {replies.length > 0 && (
                    // Indented, accent-railed group descending from the parent — the
                    // whole layout is left-aligned so the rail ties to the comment above.
                    <Box sx={{ mt: 1, ml: 2, pl: 2, borderLeft: "2px solid", borderColor: "primary.light" }}>
                      <Stack spacing={1.5}>
                        {collapsed && (
                          <Button
                            variant="text"
                            size={UIComponentSize.Small}
                            onClick={() => toggleThread(comment.id)}
                            sx={{ alignSelf: "flex-start", minWidth: 0, px: 0.5, fontSize: "0.72rem" }}
                          >
                            Show {hiddenCount} earlier {hiddenCount === 1 ? "reply" : "replies"}
                          </Button>
                        )}
                        {shownReplies.map((reply) => renderItem(reply, true))}
                        {expanded && replies.length > REPLY_COLLAPSE_THRESHOLD && (
                          <Button
                            variant="text"
                            size={UIComponentSize.Small}
                            onClick={() => toggleThread(comment.id)}
                            sx={{ alignSelf: "flex-start", minWidth: 0, px: 0.5, fontSize: "0.72rem" }}
                          >
                            Show fewer
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            No comments yet
          </Typography>
        )}
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <Divider sx={{ mt: 2 }} />

        {canWrite ? (
          <>
            {replyTarget && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: 1,
                  px: 1,
                  py: 0.5,
                  bgcolor: "action.hover",
                  borderLeft: "2px solid",
                  borderColor: "primary.light",
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Replying to <b>{authorName(replyTarget)}</b>
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Cancel reply"
                  onClick={() => setReplyTarget(null)}
                  sx={{ p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            )}
            <CommentInput value={draft} onChange={setDraft} onSubmit={handleSubmit} disabled={submitting} compact />
          </>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            {readOnly ? "This partner is read-only — comments can't be added." : "Sign in to comment."}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
